import { Trans } from "@lingui/macro";
import { useTheme } from "@mui/material";
import { useMediaQuery } from "@mui/material";
import { IPeriodUnit } from "@smp/api";
import {
  UiBox,
  UiButton,
  UiButtonGroup,
  UiSpinner,
  UiTypography,
} from "@smp/ui";
import { testAppendAttr, textCapitalize } from "@smp/utils";
import { FC, useLayoutEffect, useMemo } from "react";
import { useUpdateEffect } from "react-use";

import { useHumanizer } from "@/hooks/use_humanizer";
import { useQueryBillingPlanProductSet } from "@/queries/billing/use_query_billing_plan_product_set";
import { useContextApp } from "@/screens/app/context/app";

import { IInvoiceFormValues } from "../../use_invoice_form";
import classes from "./plan_period_selector.module.css";

export interface IPeriodSelectorProps {
  paymentType: IInvoiceFormValues["paymentType"];
  value: Extract<IInvoiceFormValues["product"], { type: "plan-product" }>;
  defaultValue: IPeriodSelectorProps["value"] | undefined;
  onChange: (value: IPeriodSelectorProps["value"]) => void;
  onChangeMonthlyPrice: (value: number | undefined) => void;
}

const isPlanValid = (
  plan: IPeriodSelectorProps["value"],
  paymentType: IPeriodSelectorProps["paymentType"],
): boolean => {
  const paymentTypeField = {
    recurring: "is_recurring_payment_allowed" as const,
    single: "is_single_payment_allowed" as const,
  }[paymentType];

  return plan[paymentTypeField];
};

export const PlanPeriodSelector: FC<IPeriodSelectorProps> = ({
  paymentType,
  value,
  defaultValue,
  onChange,
  onChangeMonthlyPrice,
}) => {
  const { humanizePeriodUnit } = useHumanizer();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const {
    state: { me },
  } = useContextApp();

  const { data: productList, isFetching: isProductListFetching } =
    useQueryBillingPlanProductSet({
      billing_policy_id: me.billing_policy_id,
      plan_id: value.plan.id,
      is_recurring_payment_allowed:
        paymentType === "recurring" ? true : undefined,
      is_single_payment_allowed: paymentType === "single" ? true : undefined,
    });

  const productListFiltered = useMemo(
    () => productList?.filter((product) => isPlanValid(product, paymentType)),
    [paymentType, productList],
  );

  const productListSorted = useMemo(
    () =>
      productListFiltered?.toSorted((a, b) => {
        if (a.period_units === b.period_units) {
          return a.period_length - b.period_length;
        }

        return a.period_units - b.period_units;
      }),
    [productListFiltered],
  );

  useLayoutEffect(() => {
    onChangeMonthlyPrice(
      productListFiltered?.find(
        (product) =>
          product.period_length === 1 &&
          product.period_units === IPeriodUnit.MONTH,
      )?.price,
    );
  }, [onChangeMonthlyPrice, productListFiltered]);

  useUpdateEffect(() => {
    const lastProduct = productListSorted?.at(-1);

    if (isPlanValid(value, paymentType)) {
      return;
    } else if (defaultValue && isPlanValid(defaultValue, paymentType)) {
      onChange(defaultValue);
    } else if (lastProduct && isPlanValid(lastProduct, paymentType)) {
      onChange(lastProduct);
    }
  }, [paymentType]);

  return (
    <>
      <UiBox mb={2}>
        <UiTypography variant="h4">
          {paymentType === "recurring" ? (
            <Trans>Charge period</Trans>
          ) : (
            <Trans>Plan period</Trans>
          )}
        </UiTypography>
      </UiBox>

      {isProductListFetching ? (
        <UiBox
          display="flex"
          height={40}
          justifyContent="center"
          alignItems="center"
        >
          <UiSpinner size={24} />
        </UiBox>
      ) : (
        <UiButtonGroup
          fullWidth
          orientation={isMobile ? "vertical" : "horizontal"}
        >
          {productListSorted?.map((product) => {
            const isActive =
              value.period_length === product.period_length &&
              value.period_units === product.period_units;

            return (
              <UiButton
                key={product.id}
                color={isActive ? "primary" : "paper"}
                onClick={() => onChange(product)}
                variant={isActive ? "contained" : "outlined"}
                {...testAppendAttr("class", `purchase_plan_period`)}
              >
                {textCapitalize(
                  humanizePeriodUnit(
                    product.period_units,
                    product.period_length,
                    product.period_length <= 1,
                  ),
                )}
                {product.period_length === 1 && product.period_units === 8 && (
                  <UiBox className={classes.badge}>
                    <UiTypography className={classes.badge__text} noWrap>
                      <Trans>
                        __dialog_purchase.prepare.periods.period__Save {33}%
                      </Trans>
                    </UiTypography>
                  </UiBox>
                )}
              </UiButton>
            );
          })}
        </UiButtonGroup>
      )}
    </>
  );
};
